import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { MeetingList } from '../components/Meetings/MeetingList';
import { MiniCalendar } from '../components/Meetings/MiniCalendar';
import { NewMeetingButton } from '../components/Meetings/NewMeetingButton';
import { MeetingCreateModal } from '../components/Meetings/MeetingCreateModal';
import { useMeetingStore } from '../store/meetingStore';
import { useAuthStore } from '../store/authStore';
import type { Meeting, CreateMeetingForm } from '../types/meetings';

export const MeetingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { meetings, isLoading, fetchMeetings, createMeeting } = useMeetingStore();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  const handleMeetingClick = (meeting: Meeting) => {
    navigate(`/rdv/${meeting.id}`);
  };

  const handleCreateMeeting = async (meetingData: CreateMeetingForm) => {
    setIsCreating(true);
    try {
      await createMeeting(meetingData);
      setIsCreateModalOpen(false);
    } catch (error: any) {
      console.error('Error creating meeting:', error);
      throw error; // Re-throw to let modal handle the error
    } finally {
      setIsCreating(false);
    }
  };

  // Filter meetings for upcoming ones (next 30 days)
  const now = new Date();
  const thirtyDaysFromNow = new Date();
  thirtyDaysFromNow.setDate(now.getDate() + 30);

  const upcomingMeetings = meetings.filter(meeting => {
    const meetingDate = new Date(meeting.start_date);
    return meetingDate >= now && meetingDate <= thirtyDaysFromNow;
  });

  // If no upcoming meetings, show recent past meetings
  const displayedMeetings = upcomingMeetings.length > 0
    ? upcomingMeetings.slice(0, 10)
    : meetings.slice(0, 10);

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Rendez-vous</h1>
            <p className="text-gray-600 mt-1">
              Gérez vos rendez-vous et planifiez de nouvelles rencontres
            </p>
          </div>
          <NewMeetingButton onClick={() => setIsCreateModalOpen(true)} />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Meeting List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {upcomingMeetings.length > 0 ? 'Prochains rendez-vous' : 'Rendez-vous récents'}
              </h2>
              <MeetingList
                meetings={displayedMeetings}
                onMeetingClick={handleMeetingClick}
                isLoading={isLoading}
              />
            </div>
          </div>

          {/* Right Column - Mini Calendar */}
          <div className="space-y-6">
            <MiniCalendar
              meetings={meetings}
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
            />

            {/* Quick Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Statistiques</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total rendez-vous</span>
                  <span className="font-medium">{meetings.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">À venir</span>
                  <span className="font-medium text-blue-600">{upcomingMeetings.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Confirmés</span>
                  <span className="font-medium text-green-600">
                    {meetings.filter(m => m.status === 'CONFIRME').length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create Meeting Modal */}
        <MeetingCreateModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateMeeting}
          isLoading={isCreating}
        />
      </div>
    </Layout>
  );
};